import {
  initMartech,
  updateUserConsent,
  martechEager,
  martechLazy,
  martechDelayed,
} from '../plugins/martech/src/index.js';
import {
  loadHeader,
  loadFooter,
  decorateAssets,
  decorateButtons,
  decorateIcons,
  decorateSections,
  decorateBlocks,
  decorateTemplateAndTheme,
  getCookie,
  getMetadata,
  waitForFirstImage,
  loadSection,
  loadSections,
  loadCSS,
  fetchPlaceholders,
  isDevHost,
  removeH6AndLeaveContent,
} from './aem.js';
import {
  checkEventPayload,
} from './analytics/analyticsEager.js';

/**
 * load fonts.css and set a session storage flag
 */
async function loadFonts() {
  await loadCSS(`${window.hlx.codeBasePath}/dist/styles/fonts.css`);
  try {
    if (!window.location.hostname.includes('localhost')) sessionStorage.setItem('fonts-loaded', 'true');
  } catch {
    // do nothing
  }
}

/**
 * Stores the referrer URL in session storage if it is different from the current hostname.
 * This function checks if the referrer is already stored in session storage.
 * If not, it extracts the hostname from the referrer URL and compares it with the current hostname.
 * If they are different, it stores the referrer URL in session storage.
 */
function storeReferrer() {
  if (sessionStorage.getItem('referrer')) return;
  let { referrer } = document;
  if (referrer) {
    const referrerURL = new URL(referrer);
    const referrerHostname = referrerURL.hostname;
    referrer = referrerHostname !== window.location.hostname ? referrer : '';
  }

  sessionStorage.setItem('referrer', referrer);
}

/**
 * Asynchronously loads a template by loading its corresponding CSS and JS files.
 *
 * @param {Document} doc - The document object where the template will be applied.
 * @param {string} templateName - The name of the template to load.
 *
 * @throws Will throw an error if the template fails to load.
 */
export async function loadTemplate(doc, templateName) {
  if (document.body.classList.contains(templateName) && ['loaded', 'loading'].includes(document.body.dataset.templateStatus)) {
    // already loaded or in the process of loading...nothing to do here
    return;
  }
  try {
    const cssPath = `${window.hlx.codeBasePath}/dist/templates/${templateName}/${templateName}.css`;
    const jsPath = `../templates/${templateName}/${templateName}.js`;

    document.body.dataset.templateStatus = 'loading';

    // Use Promise.all to load CSS and JS module concurrently
    await Promise.all([
      loadCSS(cssPath),
      (async () => {
        try {
          const mod = await import(jsPath);
          if (mod.default) {
            await mod.default(doc);
          }
        } catch (error) {
          console.error(`failed to load module for ${templateName}`, error);
        }
      })(),
    ]);
    document.body.dataset.templateStatus = 'loaded';
  } catch (error) {
    console.error(`failed to load template ${templateName}`, error);
  }
}

/**
 * Builds all synthetic blocks in a container element.
 * @param {Element} main The container element
 */
async function buildTemplate(main) {
  try {
    const templateName = getMetadata('template');
    if (templateName) {
      await loadTemplate(main, templateName.toLowerCase());
    }
  } catch (error) {
    console.error('Template loading failed', error);
  }
}

/**
 * Decorates the main element.
 * @param {Element} main The main element
 */

export function decorateMain(main) {
  // hopefully forward compatible button decoration
  decorateAssets(main);
  decorateButtons(main);
  decorateIcons(main);
  buildTemplate(main);
  decorateSections(main);
  decorateBlocks(main);
}

/**
 * Handles the consent event and updates user consent preferences.
 * @param {CustomEvent} ev - The consent event containing consent details.
 */
function consentEventHandler(ev) {
  const groups = ev.detail;
  const collect = groups.includes('C0002'); // Performance Cookies
  const personalize = groups.includes('C0003'); // Functional Cookies
  const marketing = groups.includes('C0004'); // Marketing Cookies
  const share = groups.includes('C0005'); // Targeted Advertising and Selling/Sharing of Personal Information
  updateUserConsent({
    collect,
    personalize,
    marketing,
    share,
  });
}

/**
 * Checks if personalization is enabled based on the 'OptanonConsent' cookie.
 * @returns {boolean} - Returns true if personalization (C0003) is enabled, otherwise false.
 */
function isPersonalizationEnabled() {
  const cookie = getCookie('OptanonConsent');
  if (cookie) {
    const groups = cookie.split('&').find((param) => param.startsWith('groups='));

    if (!groups) {
      return false;
    }

    const groupValues = groups.split('=')[1].split(',');
    const c0003 = groupValues.find((group) => group.startsWith('C0003'));
    return c0003.split(':')[1] === '1';
  }
  return false;
}

/**
 * Loads the languages JSON to determine if the current language is supported.
 * If the language is not supported, it defaults to 'en'.
 *
 * @returns {Promise} - A promise that resolves when the page language is set.
 */
async function setPageLanguage() {
  let language = window.location.pathname.split('/')[1]?.toString().toLowerCase();
  const availableLanguages = window.placeholders['/config'].availableLanguages?.split(',').map((elem) => elem.trim());

  if (!availableLanguages?.includes(language)) {
    language = 'en';
  }

  document.documentElement.lang = language;
}

/**
 * Syncs the selected language in session storage with the URL.
 */
function syncLanguageSessionWithURL() {
  const firstSegment = window.location.pathname.split('/')[1];
  const urlLang = /^[a-z]{2}$/.test(firstSegment) ? firstSegment : 'en';
  const storedLang = sessionStorage.getItem('selectedLanguage');

  if (storedLang !== urlLang) {
    sessionStorage.setItem('selectedLanguage', urlLang);
  }
}

const fetchTaxonomyPlaceholders = async (useDefaultConfig) => {
  let taxonomyFileLocation = window.placeholders['/config'].taxonomyPlaceholders;

  if (useDefaultConfig) {
    taxonomyFileLocation = window.placeholders['/config'].default.taxonomyPlaceholders;
  }

  let taxPlaceholders = {};

  try {
    taxPlaceholders = await (await fetch(taxonomyFileLocation)).json();
    taxPlaceholders = taxPlaceholders.data.reduce((acc, curr) => {
      (acc[curr.key] = curr.text);

      return acc;
    }, {});
  } catch {
    console.error(`Failed to load taxonomy placeholders`);
    taxPlaceholders = {};
  }

  window.placeholders = window.placeholders || {};
  window.placeholders.taxonomy = window.placeholders.taxonomy || {};
  window.placeholders.taxonomy = taxPlaceholders || {};

  return window.placeholders.taxonomy;
};

async function loadLanguageAndTaxonomyPlaceholder() {
  const currentLang = document.documentElement.lang;

  const placeholdersPromisesList = [
    fetchPlaceholders('default'),
    fetchTaxonomyPlaceholders(),
  ];

  if (currentLang !== 'en') {
    placeholdersPromisesList.push(
      fetchPlaceholders('default', currentLang),
      fetchTaxonomyPlaceholders(true),
    );
  }

  const [placeholder, langTaxPlaceholder, placeholderLangOther = {}, defaultTaxPlaceholder = {}] = await Promise.all(placeholdersPromisesList);
  // merging taxonomy placeholders with fallbacks (when no taxonomy placeholder is found for a specific language, it will fallback to the default one)
  const taxPlaceholdersWithFallbacks = new Set([...Object.keys(defaultTaxPlaceholder), ...Object.keys(langTaxPlaceholder)]);
  const taxPlaceholders = [...taxPlaceholdersWithFallbacks].reduce((acc, curr) => {
    acc[curr] = langTaxPlaceholder?.[curr] || defaultTaxPlaceholder[curr] || '';
    return acc;
  }, {});

  const placeholderWithFallbacks = new Set([...Object.keys(placeholder), ...Object.keys(placeholderLangOther)]);
  const placeholders = [...placeholderWithFallbacks].reduce((acc, curr) => {
    acc[curr] = placeholderLangOther?.[curr] || placeholder[curr] || '';
    return acc;
  }, {});

  window.placeholders.default = placeholders;
  window.placeholders.taxonomy = taxPlaceholders;
}

const loadModal = async (main) => {
  const modalsSection = [...main.querySelectorAll('.section[data-modal-hash]')];
  const modalHashesList = modalsSection.map((section) => section.dataset.modalHash);
  const isModalNeeded = modalsSection.length;
  let modalFunctions = null;

  if (!isModalNeeded) {
    return;
  }

  const modalImport = await (import('./helpers/modal/modal.js'));
  modalFunctions = modalImport.default();

  modalsSection.forEach((section) => {
    const hashValue = section.getAttribute('data-modal-hash');
    const modalStyles = section.getAttribute('data-modal-styles');
    const modalVariant = section.getAttribute('data-modal-variant');
    const classes = [modalStyles, modalVariant].filter(Boolean);

    modalFunctions.addModalContent(hashValue, section, { classes });
  });

  const onHashChange = () => {
    const { hash } = window.location;
    const hashValue = hash.substring(1);

    if (modalHashesList.includes(hashValue)) {
      modalFunctions.showModal(hashValue);
    } else {
      modalFunctions.hideModal();
    }
  };

  const onHashChangeWithCookieCollisionAvoidance = () => {
    const isOptanonAlertBoxClosed = !!getCookie('OptanonAlertBoxClosed');
    if (isOptanonAlertBoxClosed) {
      onHashChange();
    } else {
      const cookieChangeHandler = (event) => {
        if (event.changed.some((c) => c.name === 'OptanonAlertBoxClosed')) {
          onHashChange();
          window.cookieStore.removeEventListener('change', cookieChangeHandler);
        }
      };
      window.cookieStore.addEventListener('change', cookieChangeHandler);
    }
  };

  window.addEventListener('hashchange', () => {
    onHashChangeWithCookieCollisionAvoidance();
  });
  onHashChangeWithCookieCollisionAvoidance();
};

/**
 * Loads everything needed to get to LCP.
 * @param {Element} doc The container element
 */
async function loadEager(doc) {
  window.adobeDataLayer = window.adobeDataLayer || [];
  window.blockErrors = [];
  const config = await fetchPlaceholders('/config');
  const isConsentGiven = isPersonalizationEnabled();
  const martechLoadedPromise = initMartech(
    // The WebSDK config
    {
      datastreamId: `${isDevHost() ? config.devDatastreamId : config.prodDatastreamId}`,
      orgId: config.orgId,
      targetMigrationEnabled: true,
      context: ['web', 'device', 'environment'],
      debugEnabled: isDevHost(),
      edgeDomain: config.edgeDomain,
      clickCollectionEnabled: false,
      onBeforeEventSend: (payload) => {
        payload.data.__adobe.target ||= {};
        payload.data.__adobe.analytics ||= {};
        checkEventPayload(payload);
      },
      edgeConfigOverrides: {},
    },
    // The library config
    {
      launchUrls: [`${isDevHost() ? config.testingLaunch : config.prodLaunch}`],
      personalization: isConsentGiven,
      includeDataLayerState: false,
    },
  );
  setPageLanguage();
  syncLanguageSessionWithURL();
  decorateTemplateAndTheme();
  const main = doc.querySelector('main');
  if (main) {
    decorateMain(main);
    await loadLanguageAndTaxonomyPlaceholder();
    await Promise.all([
      martechLoadedPromise.then(martechEager),
      document.body.classList.add('appear'),
      loadSection(main.querySelector('.section'), waitForFirstImage),
    ]);
    loadModal(main);
  }

  try {
    /* if desktop (proxy for fast connection) or fonts already loaded, load fonts.css */
    if (window.innerWidth >= 900 || sessionStorage.getItem('fonts-loaded')) {
      loadFonts();
    }
  } catch {
    // do nothing
  }
}

/**
 * Loads everything that doesn't need to be delayed.
 * @param {Element} doc The container element
 */
async function loadLazy(doc) {
  const Notification = await (import('./helpers/notifications/notification.js'));
  const ContextualNotifications = await (import('./helpers/contextual-notifications/contextual-notifications.js'));

  new Notification.default();
  const main = doc.querySelector('main');
  await loadSections(main);

  const { hash } = window.location;
  const element = hash ? doc.getElementById(hash.substring(1)) : false;
  if (hash && element) element.scrollIntoView();

  loadHeader(doc.querySelector('header'));
  loadFooter(doc.querySelector('footer'));

  ContextualNotifications.loadContextualNotifications(main);

  loadCSS(`${window.hlx.codeBasePath}/dist/styles/lazy-styles.css`);
  loadFonts();
  storeReferrer();
  await martechLazy();
  const { initAnalytics } = await import('./analytics/analyticsLazy.js');
  initAnalytics();
}

/**
 * Loads everything that happens a lot later,
 * without impacting the user experience.
 */
function loadDelayed() {
  window.setTimeout(async () => {
    martechDelayed();
    import('./delayed.js');
    const { default: initAnalyticsDelayed } = await import('./analytics/analyticsDelayed.js');
    initAnalyticsDelayed();
  }, 3000);
}

async function loadPage() {
  const main = document.querySelector('main');
  removeH6AndLeaveContent(main);
  if (main) {
    await loadEager(document);
    await loadLazy(document);
    loadDelayed();
  }
}

loadPage();
window.addEventListener('consent.onetrust', consentEventHandler);

function onPersistedPageShow() {
  window.persistedPageShowListeners = [];

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      syncLanguageSessionWithURL();
      location.reload();
      window.persistedPageShowListeners.forEach((listener) => {
        listener();
      });
    }
  });
};

onPersistedPageShow();
